/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useLayoutEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '@store/index'
import {
  Accordion,
  Dropdown,
  OverlayTrigger,
  Tooltip,
  Form,
} from 'react-bootstrap'
import SearchWithFilter, {
  FilterSelection,
} from '@components/common/SearchWithFilter/SearchWithFilter.component'
import { StatusTypes } from '@components/common/StatusBadge/StatusBadge.component'
import {
  deleteAppointmentById,
  getAppointmentsByPatientId,
  updateAppointmentById,
} from '@services/api/appointment.api'
import moment from 'moment'
import {
  AppointmentRequest,
  AppointmentResponse,
  AppointmentStatus,
} from '@dataTypes/appointment.type'
import { Patient } from '@dataTypes/PatientInfo.types'
import { ScheduleAppointment } from '../Modals'
import ConfirmationModal from '@components/common/ConfirmationModal/ConfirmationModal.component'
import {
  deleteModalBody,
  deleteModalFooter,
} from '@components/pages/Appointments/DayCalendar/DayCalendarTemplate'
import { useAppSelector } from '@store/hooks'
import { refreshFromPatient } from '@store/reducers/patientmanagement.reducer'
import { mockAppointments } from '@assets/mockdata/PatientTray/mockAppointments'
import { shouldUsePatientTrayMockData } from '@utils/patientTrayMocks.util'
import { DateRangePickerComponent } from '@syncfusion/ej2-react-calendars'
import { htmlToPlainTextPreview } from '@utils/htmlPlainText.util'
import {
  formatAppointmentLocalDate,
  formatAppointmentLocalStartTime,
  getAppointmentLocalMoment,
} from '@utils/time.util'
import { ReactComponent as ArrowRightMonthIcon } from '@assets/images/Sidepanel/arrow-right-month.svg'
import { ReactComponent as ArrowDownMonthIcon } from '@assets/images/Sidepanel/arrow-down-month.svg'
import { AppointmentStatusMap } from '@utils/appointment.util'
import {
  Badge,
  EmptyState,
  type BadgeColor,
  type BadgeMode,
} from '@enamelai/design-system'
import { useTheme } from '@context/ThemeContext'

export const editDeleteDDComponent = (
  id?: string,
  onEdit?: (id: string) => void,
  onDelete?: (id: string) => void,
): JSX.Element => {
  return (
    <Dropdown className="" style={{ marginBottom: '2px' }}>
      <Dropdown.Toggle
        variant="light"
        id="dropdown-history-sidepanel"
        className="edit-delete-dd"
      >
        <i className="ti ti-dots-vertical"></i>
      </Dropdown.Toggle>
      <Dropdown.Menu className="edit-delete-menu">
        <Dropdown.Item onClick={onEdit && (() => onEdit(id || ''))}>
          <i className="ti ti-pencil me-1"></i>Edit
        </Dropdown.Item>
        <Dropdown.Item onClick={onDelete && (() => onDelete(id || ''))}>
          <i className="ti ti-trash me-1"></i>Delete
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  )
}

type HistoryProps = {
  patient: Patient | null
}

export const HistorySectionHeader: React.FC<{ title: string }> = ({
  title,
}) => (
  <div className="sp-history-section-header">
    <span className="sp-history-section-title">{title}</span>
  </div>
)

export type HistoryMonthGroup = {
  key: string
  label: string
  appointments: AppointmentResponse[]
}

/**
 * Groups appointments by calendar month (year-aware, so Dec 2026 and Dec 2027
 * never merge) in the order given by `appointments`. Months outside the
 * current year get the year appended to the label.
 */
export const groupAppointmentsByMonth = (
  appointments: AppointmentResponse[],
): HistoryMonthGroup[] => {
  const currentYear = moment().year()
  const groups = new Map<string, HistoryMonthGroup>()
  appointments.forEach((appointment) => {
    const local = getAppointmentLocalMoment(appointment)
    const key = local.format('YYYY-MM')
    let group = groups.get(key)
    if (!group) {
      group = {
        key,
        label:
          local.year() === currentYear
            ? local.format('MMMM')
            : local.format('MMMM YYYY'),
        appointments: [],
      }
      groups.set(key, group)
    }
    group.appointments.push(appointment)
  })
  return Array.from(groups.values())
}

type HistoryMonthAccordionProps = {
  groups: HistoryMonthGroup[]
  renderAppointment: (
    appointment: AppointmentResponse,
    index: number,
  ) => React.ReactNode
}

/**
 * Per-month accordion used by the Upcoming section of the patient and staff
 * side panels. The first month is open by default, and re-opens whenever the
 * set of months changes (new data or filters); a month the user collapsed
 * stays closed otherwise.
 */
export const HistoryMonthAccordion: React.FC<HistoryMonthAccordionProps> = ({
  groups,
  renderAppointment,
}) => {
  const [activeKey, setActiveKey] = useState<string | null>(
    groups[0]?.key ?? null,
  )
  const groupKeys = groups.map((group) => group.key).join('|')

  useEffect(() => {
    setActiveKey(groupKeys ? groupKeys.split('|')[0] : null)
  }, [groupKeys])

  return (
    <Accordion
      activeKey={activeKey}
      onSelect={(key) =>
        setActiveKey(Array.isArray(key) ? (key[0] ?? null) : (key ?? null))
      }
      className="sp-history-month-accordion"
    >
      {groups.map((group) => (
        <Accordion.Item eventKey={group.key} key={group.key}>
          <Accordion.Header>
            <span className="sp-history-month-arrow" aria-hidden>
              <ArrowRightMonthIcon className="sp-history-month-arrow-icon sp-history-month-arrow-icon--right" />
              <ArrowDownMonthIcon className="sp-history-month-arrow-icon sp-history-month-arrow-icon--down" />
            </span>
            <span className="sp-history-month-label">{group.label}</span>
          </Accordion.Header>
          <Accordion.Body>
            <div className="sp-history-appt-list sp-history-appt-list--nested">
              {group.appointments.map(renderAppointment)}
            </div>
          </Accordion.Body>
        </Accordion.Item>
      ))}
    </Accordion>
  )
}

const HistoryApptCardSkeleton: React.FC = () => (
  <div className="sp-history-skeleton-card">
    <div className="sp-history-skeleton-row">
      <span className="sp-history-skeleton-bar sp-history-skeleton-bar--title" />
      <span className="sp-history-skeleton-bar sp-history-skeleton-bar--badge" />
    </div>
    <div className="sp-history-skeleton-row">
      <span className="sp-history-skeleton-bar sp-history-skeleton-bar--meta" />
      <span className="sp-history-skeleton-bar sp-history-skeleton-bar--time" />
    </div>
  </div>
)

export const HistoryUpcomingSkeleton: React.FC = () => (
  <div className="sp-history-skeleton" aria-hidden="true">
    <HistoryApptCardSkeleton />
    <HistoryApptCardSkeleton />
  </div>
)

export const HistoryPreviousSkeleton: React.FC = () => (
  <div className="sp-history-skeleton" aria-hidden="true">
    {[0, 1, 2].map((key) => (
      <div key={key} className="sp-history-skeleton-month">
        <span className="sp-history-skeleton-bar sp-history-skeleton-bar--month" />
      </div>
    ))}
  </div>
)

export const getHistoryStatusColor = (
  status: StatusTypes,
  isUpcoming: boolean,
): BadgeColor => {
  switch (status) {
    case StatusTypes.Confirmed:
    case StatusTypes.CheckIn:
      return 'pale-green'
    case StatusTypes.Completed:
      return 'vivid-green'
    case StatusTypes.Pending:
      return isUpcoming ? 'red' : 'orange'
    case StatusTypes.Alert:
    case StatusTypes.Cancelled:
    case StatusTypes.Late:
      return 'red'
    case StatusTypes.NoShow:
    case StatusTypes.Unconfirmed:
      return 'yellow'
    case StatusTypes.FollowUp:
    case StatusTypes.InTreatment:
      return 'blue'
    case StatusTypes.NewPatient:
    case StatusTypes.CheckOut:
      return 'teal'
    default:
      return 'gray'
  }
}

export const getHistoryStatusLabel = (status: StatusTypes): string => {
  switch (status) {
    case StatusTypes.FollowUp:
      return 'Follow-up'
    case StatusTypes.NewPatient:
      return 'New patient'
    case StatusTypes.NoShow:
      return 'No show'
    case StatusTypes.InTreatment:
      return 'In treatment'
    case StatusTypes.PatientLate:
      return 'Patient late'
    case StatusTypes.InvoiceSent:
      return 'Invoice sent'
    case StatusTypes.PaymentPending:
      return 'Payment pending'
    case StatusTypes.RevisionNeeded:
      return 'Revision needed'
    case StatusTypes.UnderReview:
      return 'Under review'
    case StatusTypes.PartiallyApproved:
      return 'Partially approved'
    case StatusTypes.OutForDelivery:
      return 'Out for delivery'
    default:
      return status
  }
}

const History: React.FC<HistoryProps> = ({ patient }): React.ReactElement => {
  const dispatch: AppDispatch = useDispatch()
  const { resolvedTheme } = useTheme()
  const badgeMode: BadgeMode = resolvedTheme === 'dark' ? 'solid' : 'tinted'
  const [previousAppointmentsList, setPreviousAppointmentsList] = useState<
    AppointmentResponse[]
  >([])
  const [upcomingAppointmentsList, setUpcomingAppointmentsList] = useState<
    AppointmentResponse[]
  >([])
  const [searchText, setSearchText] = useState<string>('')
  const [currentAppt, setCurrentAppt] = useState<AppointmentResponse | null>(
    null,
  )
  const [isSchedulingModalOpen, setIsSchedulingModalOpen] =
    useState<boolean>(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)
  const [apptIdToDelete, setApptIdToDelete] = useState<string | null>(null)
  const [selectedFilters, setSelectedFilters] = useState<FilterSelection>({})
  const [filterResetKey, setFilterResetKey] = useState<number>(0)
  const [activeAccordionKey, setActiveAccordionKey] = useState<string | null>(
    null,
  )
  const [customDateRange, setCustomDateRange] = useState<{
    start: Date
    end: Date
  }>({
    start: moment().subtract(3, 'months').startOf('month').toDate(),
    end: moment().toDate(),
  })
  const refresh = useAppSelector((state) => state.patient.refresh)
  const [loadedPatientId, setLoadedPatientId] = useState<string | null>(null)
  const isLoading = Boolean(patient) && loadedPatientId !== patient?.patient_id
  const hasNoAppointments =
    !isLoading &&
    upcomingAppointmentsList.length === 0 &&
    previousAppointmentsList.length === 0

  const [historyTooltipContainer, setHistoryTooltipContainer] =
    useState<HTMLDivElement | null>(null)

  useLayoutEffect(() => {
    const el = document.createElement('div')
    el.className = 'history-tooltip-portal-root'
    el.setAttribute('data-history-tooltip-portal', '')
    document.body.appendChild(el)
    setHistoryTooltipContainer(el)
    return () => {
      el.remove()
    }
  }, [])

  const statusOptions: { label: string; value: AppointmentStatus }[] = [
    { label: 'Confirmed', value: AppointmentStatus.Confirmed },
    { label: 'Completed', value: AppointmentStatus.Completed },
    { label: 'Pending', value: AppointmentStatus.Pending },
    { label: 'Follow-up', value: AppointmentStatus.FollowUp },
    { label: 'New patient', value: AppointmentStatus.NewPatient },
    { label: 'Cancelled', value: AppointmentStatus.Cancelled },
    { label: 'No show', value: AppointmentStatus.NoShow },
    { label: 'Unconfirmed', value: AppointmentStatus.Unconfirmed },
    { label: 'Check-in', value: AppointmentStatus.CheckIn },
    { label: 'In treatment', value: AppointmentStatus.InTreatment },
    { label: 'Check-out', value: AppointmentStatus.CheckOut },
  ]

  const statusLabelByValue: Record<AppointmentStatus, string> =
    statusOptions.reduce(
      (acc, curr) => {
        acc[curr.value] = curr.label
        return acc
      },
      {} as Record<AppointmentStatus, string>,
    )

  const monthOptions = [
    'Past 3 months',
    'Past 6 months',
    'Past 12 months',
    'Custom',
  ]
  const monthLookup: Record<string, number> = {
    'Past 3 months': 3,
    'Past 6 months': 6,
    'Past 12 months': 12,
  }

  const appointmentTypeOptions = useMemo(() => {
    const allAppointments = [
      ...previousAppointmentsList,
      ...upcomingAppointmentsList,
    ]
    const uniqueTypes = new Set<string>()
    allAppointments.forEach((appt) => {
      if (appt.appointmenttype?.name) {
        uniqueTypes.add(appt.appointmenttype.name)
      }
    })
    return Array.from(uniqueTypes).sort()
  }, [previousAppointmentsList, upcomingAppointmentsList])

  const onEditAppointment = (apptId: string): void => {
    setCurrentAppt(
      upcomingAppointmentsList.find((appt) => appt.appointment_id === apptId) ||
        previousAppointmentsList.find(
          (appt) => appt.appointment_id === apptId,
        ) ||
        null,
    )
    setIsSchedulingModalOpen(true)
  }

  const onDeleteAppointment = (apptId: string): void => {
    setApptIdToDelete(apptId)
    setIsDeleteModalOpen(true)
  }

  const appointmentCardElement = (
    appt: AppointmentResponse,
    isUpcoming: boolean,
  ): JSX.Element => {
    const statusType =
      AppointmentStatusMap[appt.appt_status as AppointmentStatus]

    return (
      <div key={appt.appointment_id} className="sp-history-appt-card">
        <div className="sp-history-appt-card-top">
          <div className="sp-history-appt-title-row">
            <span className="sp-history-appt-type">
              {appt.appointmenttype.name}
            </span>
            <span className="sp-history-appt-provider">
              {`Dr. ${appt.provider.last_name}`}
            </span>
          </div>
          <span className="sp-history-appt-date">
            {formatAppointmentLocalDate(appt, 'MM-DD-YYYY')}
          </span>
        </div>
        <div className="sp-history-appt-card-bottom">
          <Badge
            label={getHistoryStatusLabel(statusType)}
            color={getHistoryStatusColor(statusType, isUpcoming)}
            mode={badgeMode}
          />
          {editDeleteDDComponent(
            appt.appointment_id,
            onEditAppointment,
            onDeleteAppointment,
          )}
        </div>
      </div>
    )
  }

  const historyTooltip = (
    data: AppointmentResponse,
    index: number,
    isUpcoming: boolean,
  ): JSX.Element => {
    return (
      <OverlayTrigger
        key={index}
        placement="top"
        transition={false}
        container={historyTooltipContainer ?? undefined}
        popperConfig={{ strategy: 'fixed' }}
        overlay={
          <Tooltip id={`tooltip-${index}`} className="history-tt-custom">
            <div className="d-flex mb-1 tt-title w-100">Appointment info</div>

            <div className="d-flex mb-1 w-100">
              <div className="w-50 text-end pe-2">
                <strong className="tt-label ">Appt. type:</strong>
              </div>
              <div className="w-50 text-start tt-value">
                {data.appointmenttype.name}
              </div>
            </div>

            <div className="d-flex mb-1 w-100">
              <div className="w-50 text-end pe-2">
                <strong className="tt-label">Date & time:</strong>
              </div>
              <div className="w-50 text-start tt-value">
                <div>{formatAppointmentLocalDate(data, 'MMM DD, YYYY')}</div>
                <div>{formatAppointmentLocalStartTime(data)}</div>
              </div>
            </div>

            <div className="d-flex mb-1 w-100">
              <div className="w-50 text-end pe-2">
                <strong className="tt-label">Procedure:</strong>
              </div>
              <div className="w-50 text-start tt-value">
                {data.procedure_logs
                  .map((procedure) => procedure.proc_code)
                  .join(',')}
              </div>
            </div>

            <div className="d-flex mb-1 w-100">
              <div className="w-50 text-end pe-2">
                <strong className="tt-label">Status:</strong>
              </div>
              <div className="w-50 text-start tt-value">
                <Badge
                  label={getHistoryStatusLabel(
                    AppointmentStatusMap[data.appt_status as AppointmentStatus],
                  )}
                  color={getHistoryStatusColor(
                    AppointmentStatusMap[data.appt_status as AppointmentStatus],
                    isUpcoming,
                  )}
                  mode={badgeMode}
                />
              </div>
            </div>

            <div className="d-flex mb-1 w-100">
              <div className="w-50 text-end pe-2">
                <strong className="tt-label">Note:</strong>
              </div>
              <div className="w-50 text-start tt-value">
                <span className="">
                  {htmlToPlainTextPreview(data.notes?.[0]?.body)}
                </span>
              </div>
            </div>
          </Tooltip>
        }
      >
        {appointmentCardElement(data, isUpcoming)}
      </OverlayTrigger>
    )
  }

  useEffect(() => {
    setPreviousAppointmentsList([])
    setUpcomingAppointmentsList([])
  }, [patient?.patient_id])

  useEffect(() => {
    let cancelled = false

    const fetchAppointments = async (): Promise<void> => {
      if (!patient) return

      const patientId = patient.patient_id
      try {
        const useMockData = shouldUsePatientTrayMockData(patientId)

        if (useMockData) {
          const mockAppts = mockAppointments[patientId] || []
          const currentDate = new Date()

          const previous = mockAppts.filter((appt) =>
            getAppointmentLocalMoment(appt).isBefore(currentDate, 'day'),
          )
          const upcoming = mockAppts.filter((appt) =>
            getAppointmentLocalMoment(appt).isSameOrAfter(currentDate, 'day'),
          )

          if (cancelled) return
          setPreviousAppointmentsList(previous)
          setUpcomingAppointmentsList(upcoming)
        } else {
          const today = moment().format('YYYY-MM-DD')
          const [responsePrevious, responseUpcoming] = await Promise.all([
            getAppointmentsByPatientId(patientId, today, undefined),
            getAppointmentsByPatientId(patientId, undefined, today),
          ])

          if (cancelled) return
          setPreviousAppointmentsList(
            responsePrevious?.data?.appointments || [],
          )
          setUpcomingAppointmentsList(
            responseUpcoming?.data?.appointments || [],
          )
        }
      } finally {
        if (!cancelled) setLoadedPatientId(patientId)
      }
    }
    fetchAppointments()

    return () => {
      cancelled = true
    }
  }, [refresh, patient])

  const groupByApptDate = (
    appointmentsData: AppointmentResponse[],
  ): Array<[string, AppointmentResponse[]]> => {
    const formatter = new Intl.DateTimeFormat('en-US', { month: 'long' })

    const grouped = appointmentsData.reduce(
      (acc, appointment) => {
        const monthName = formatter.format(
          getAppointmentLocalMoment(appointment).toDate(),
        )

        if (!acc[monthName]) {
          acc[monthName] = []
        }

        acc[monthName].push(appointment)
        return acc
      },
      {} as Record<string, AppointmentResponse[]>,
    )

    return Object.entries(grouped).sort(([, aAppts], [, bAppts]) => {
      const aLatest = Math.max(
        ...aAppts.map((appt) => getAppointmentLocalMoment(appt).valueOf()),
      )
      const bLatest = Math.max(
        ...bAppts.map((appt) => getAppointmentLocalMoment(appt).valueOf()),
      )
      return bLatest - aLatest
    })
  }

  const filterAppointments = (
    appointmentsData: AppointmentResponse[],
    isUpcoming: boolean = false,
  ): AppointmentResponse[] => {
    const currentDate = new Date()
    const monthSelections = selectedFilters['Months'] || []
    const selectedStatuses = new Set(selectedFilters['Status'] || [])
    const selectedAppointmentTypes = new Set(
      selectedFilters['Appointment type'] || [],
    )

    const filteredAppointments = appointmentsData.filter((appointment) => {
      const appointmentDate = getAppointmentLocalMoment(appointment)
      const lastName = appointment.provider?.last_name || ''
      const apptType = appointment.appointmenttype?.name || ''
      const searchFilter =
        lastName.toLowerCase().includes(searchText.toLowerCase()) ||
        apptType.toLowerCase().includes(searchText.toLowerCase())
      const statusLabel =
        statusLabelByValue[appointment.appt_status as AppointmentStatus]

      let monthIncluded = true
      if (!isUpcoming && monthSelections.length > 0) {
        if (monthSelections.includes('Custom')) {
          const appointmentDateOnly = getAppointmentLocalMoment(appointment)
            .startOf('day')
            .toDate()
          const rangeStart = moment(customDateRange.start)
            .startOf('day')
            .toDate()
          const rangeEnd = moment(customDateRange.end).endOf('day').toDate()
          monthIncluded =
            appointmentDateOnly >= rangeStart && appointmentDateOnly <= rangeEnd
        } else {
          const validSelections = monthSelections.filter(
            (label) => label !== 'Custom',
          )
          if (validSelections.length > 0) {
            const subtractMonths = Math.max(
              ...validSelections.map((label) => monthLookup[label] || 3),
            )
            const subtractMonthsAgo = moment(currentDate)
              .subtract(subtractMonths, 'months')
              .startOf('month')
              .toDate()
            const appointmentMonthStart = appointmentDate
              .startOf('month')
              .toDate()
            monthIncluded =
              appointmentMonthStart >= subtractMonthsAgo &&
              appointmentMonthStart < currentDate
          }
        }
      }

      const statusIncluded =
        selectedStatuses.size === 0 || selectedStatuses.has(statusLabel)

      const appointmentTypeIncluded =
        selectedAppointmentTypes.size === 0 ||
        selectedAppointmentTypes.has(apptType)

      return (
        monthIncluded &&
        statusIncluded &&
        appointmentTypeIncluded &&
        searchFilter
      )
    })

    return filteredAppointments
  }

  // Re-open the first month only when the list or filters change, so a
  // user-collapsed item stays closed.
  useEffect(() => {
    if (previousAppointmentsList.length === 0) return
    const filteredAppointments = filterAppointments(previousAppointmentsList)
    const groupedAppointments = groupByApptDate(filteredAppointments)
    setActiveAccordionKey(groupedAppointments.length > 0 ? '0' : null)
  }, [previousAppointmentsList, selectedFilters, searchText, customDateRange])

  // Upcoming, soonest first, grouped by month (first month open by default).
  const upcomingMonthGroups = groupAppointmentsByMonth(
    filterAppointments(upcomingAppointmentsList, true)
      .slice()
      .sort(
        (a, b) =>
          getAppointmentLocalMoment(a).valueOf() -
          getAppointmentLocalMoment(b).valueOf(),
      ),
  )

  const previousAppointmentsElement = (): JSX.Element[] => {
    const groupedAppointments = groupByApptDate(
      filterAppointments(previousAppointmentsList),
    )
    if (groupedAppointments.length === 0) {
      return [
        <span key="no-appointments" className="sp-history-empty">
          No records to display
        </span>,
      ]
    }
    return groupedAppointments.map(([month, appointmentsData], idx) => {
      return (
        <Accordion.Item eventKey={idx.toString()} key={`${month}-${idx}`}>
          <Accordion.Header>
            <span className="sp-history-month-arrow" aria-hidden>
              <ArrowRightMonthIcon className="sp-history-month-arrow-icon sp-history-month-arrow-icon--right" />
              <ArrowDownMonthIcon className="sp-history-month-arrow-icon sp-history-month-arrow-icon--down" />
            </span>
            <span className="sp-history-month-label">{month}</span>
          </Accordion.Header>
          <Accordion.Body>
            <div className="sp-history-appt-list sp-history-appt-list--nested">
              {appointmentsData
                .slice()
                .sort(
                  (a, b) =>
                    getAppointmentLocalMoment(b).valueOf() -
                    getAppointmentLocalMoment(a).valueOf(),
                )
                .map((item, itemIdx) => {
                  return historyTooltip(item, itemIdx, false)
                })}
            </div>
          </Accordion.Body>
        </Accordion.Item>
      )
    })
  }

  const handleSaveAppointment = (
    apptDetails: Partial<AppointmentRequest>,
  ): void => {
    if (apptDetails?.appointment_id) {
      const isMockAppointment =
        apptDetails.appointment_id?.startsWith('mock-appt-')
      if (isMockAppointment) {
        setIsSchedulingModalOpen(false)
        return
      }

      updateAppointmentById(apptDetails.appointment_id, apptDetails).then(() =>
        dispatch(refreshFromPatient()),
      )
    }
  }

  const handleDeleteAppointment = (apptId: string): void => {
    const isMockAppointment = apptId?.startsWith('mock-appt-')
    if (isMockAppointment) {
      setIsDeleteModalOpen(false)
      return
    }

    deleteAppointmentById(apptId).then(() => {
      setIsDeleteModalOpen(false)
      dispatch(refreshFromPatient())
    })
  }

  const customDateRangeRenderer = (
    option: string,
    isChecked: boolean,
    onChange: () => void,
  ): React.ReactNode => {
    return (
      <div
        className="custom-date-range-filter"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="d-flex align-items-center gap-2">
          <Form.Check
            type="checkbox"
            label={option}
            checked={isChecked}
            onChange={onChange}
            className="mb-0"
          />
        </div>
        {isChecked && (
          <div className="custom-date-range-picker-inline">
            <DateRangePickerComponent
              placeholder="Select date range"
              value={[customDateRange.start, customDateRange.end]}
              change={(e: any) => {
                if (e.startDate && e.endDate) {
                  setCustomDateRange({ start: e.startDate, end: e.endDate })
                }
              }}
              select={(e: any) => {
                if (e.startDate && e.endDate) {
                  setCustomDateRange({ start: e.startDate, end: e.endDate })
                }
              }}
              format="MM/dd/yyyy"
              showClearButton={false}
              openOnFocus={true}
              allowEdit={false}
            />
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      {hasNoAppointments ? (
        <EmptyState
          className="sp-history-empty-state"
          iconClass="mgc mgc_calendar_fill"
          title="No appointments found"
          description="This patient doesn't have any appointments on record yet."
        />
      ) : (
        <div className="sp-history" aria-busy={isLoading}>
          <section className="sp-history-section">
            <HistorySectionHeader title="Upcoming appointments" />
            <div className="sp-history-search">
              <SearchWithFilter
                key={filterResetKey}
                className="sp-history-search-swf"
                searchIcon="mgc mgc_search_2_line"
                filterIcon="mgc mgc_settings_2_line"
                placeholder="Search"
                searchQuery={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                filters={{
                  Status: statusOptions.map((option) => option.label),
                  Months: monthOptions,
                  'Appointment type': appointmentTypeOptions,
                }}
                filterLabels={{
                  Status: 'Status',
                  Months: 'Months',
                  'Appointment type': 'Appointment type',
                }}
                onFilterChange={(filters) => setSelectedFilters(filters)}
                customRenderers={{
                  'Months:Custom': customDateRangeRenderer,
                }}
                showResetButton
                onReset={() => {
                  setSelectedFilters({})
                  setSearchText('')
                  setCustomDateRange({
                    start: moment()
                      .subtract(3, 'months')
                      .startOf('month')
                      .toDate(),
                    end: moment().toDate(),
                  })
                  setFilterResetKey((prev) => prev + 1)
                }}
              />
            </div>
            {isLoading ? (
              <div className="sp-history-appt-list">
                <HistoryUpcomingSkeleton />
              </div>
            ) : upcomingMonthGroups.length > 0 ? (
              <HistoryMonthAccordion
                groups={upcomingMonthGroups}
                renderAppointment={(appt, index) =>
                  historyTooltip(appt, index, true)
                }
              />
            ) : (
              <div className="sp-history-appt-list">
                <span className="sp-history-empty">
                  No upcoming appointments
                </span>
              </div>
            )}
          </section>

          <section className="sp-history-section sp-history-section--previous">
            <HistorySectionHeader title="Previous appointments" />
            {isLoading ? (
              <HistoryPreviousSkeleton />
            ) : (
              <Accordion
                activeKey={activeAccordionKey}
                onSelect={(eventKey) =>
                  setActiveAccordionKey(eventKey as string | null)
                }
                className="sp-history-month-accordion"
              >
                {previousAppointmentsElement()}
              </Accordion>
            )}
          </section>
        </div>
      )}
      {isSchedulingModalOpen && (
        <ScheduleAppointment
          isOpen={isSchedulingModalOpen}
          handleModal={() => setIsSchedulingModalOpen(false)}
          onSave={handleSaveAppointment}
          initialStartTime={moment().format('YYYY-MM-DD')}
          initialEndTime={moment().format('YYYY-MM-DD')}
          initialRoom={undefined}
          initialProvider={undefined}
          currentAppt={currentAppt || undefined}
        />
      )}
      <ConfirmationModal
        show={isDeleteModalOpen}
        onHide={() => setIsDeleteModalOpen(false)}
        title="Cancel schedule"
        className="confirmation-modal--alert"
        headerClassName="confirmation-modal__header"
        size="md"
        body={
          apptIdToDelete !== null ? (
            deleteModalBody(apptIdToDelete, [
              ...upcomingAppointmentsList,
              ...previousAppointmentsList,
            ])
          ) : (
            <div></div>
          )
        }
        footer={
          apptIdToDelete !== null ? (
            deleteModalFooter(
              apptIdToDelete,
              dispatch,
              () => setIsDeleteModalOpen(false),
              () => handleDeleteAppointment(apptIdToDelete),
            )
          ) : (
            <div></div>
          )
        }
      />
    </>
  )
}

export default History
